import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  Search, Users, CheckCircle, XCircle, Clock, 
  ArrowLeft, Filter, FileText, UserCheck, Activity, 
  ShieldAlert, BookOpen, AlertTriangle, Briefcase,
  MapPin, Phone, Calendar
} from 'lucide-react';

// --- API URL ---
const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxZL9N5rxe6nV1ZGoFHKTf36URSy2PzuBctC4T40A4cbDx8rSfUqXSTgN0Z8KhclQnFcw/exec"; 

// ─── THEME STYLES (AGGRESSIVE GLOBAL DARK MODE) ─────────────────────────────
const themeStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500;600&display=swap');

  /* Fallback / Light Mode Variables */
  :root, body, html, .nova-admin-wrapper {
    --bg-page: #F3F4F6;
    --bg-card: #ffffff;
    --bg-panel: #F9FAFB;
    --bg-panel-alt: #F3F4F6;
    --text-strong: #111827;
    --text-main: #374151;
    --text-secondary: #6B7280;
    --text-muted: #9CA3AF;
    --border-main: #E5E7EB;
    --border-light: #F3F4F6;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.05);
    --shadow-md: 0 10px 30px rgba(0,0,0,0.08);
    --shadow-hover: 0 4px 20px rgba(0,0,0,0.08);

    --accent-blue: #2563EB;
    --accent-green: #059669;
    --accent-red: #DC2626;
    --accent-amber: #D97706;
    --accent-purple: #7C3AED;
    --accent-cyan: #0891B2;

    /* Status Colors - Light */
    --st-sup-bg: #ede9fe; --st-sup-txt: #6d28d9; --st-sup-border: rgba(124, 58, 237, 0.3); --st-sup-grad: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%);
    --st-fo-bg: #d1fae5;  --st-fo-txt: #065f46;  --st-fo-border: rgba(5, 150, 105, 0.3);   --st-fo-grad: linear-gradient(135deg, #059669 0%, #34d399 100%);
    --st-rej-bg: #fee2e2; --st-rej-txt: #991b1b; --st-rej-border: rgba(220, 38, 38, 0.3);  --st-rej-grad: linear-gradient(135deg, #dc2626 0%, #f87171 100%);
    --st-pen-bg: #fef3c7; --st-pen-txt: #92400e; --st-pen-border: rgba(217, 119, 6, 0.3);  --st-pen-grad: linear-gradient(135deg, #d97706 0%, #fbbf24 100%);
  }

  /* Extreme Aggressive Dark Mode Selectors */
  html.dark, html.dark-mode, html[data-theme='dark'], html[data-bs-theme='dark'],
  body.dark, body.dark-mode, body[data-theme='dark'], body[data-bs-theme='dark'],
  .dark, .dark-mode, [data-theme='dark'], [data-bs-theme='dark'],
  .dark .nova-admin-wrapper, .dark-mode .nova-admin-wrapper, [data-theme='dark'] .nova-admin-wrapper, [data-bs-theme='dark'] .nova-admin-wrapper,
  .nova-admin-wrapper.dark, .nova-admin-wrapper[data-theme='dark'] {
    --bg-page: #0b1437 !important;
    --bg-card: #111c44 !important;
    --bg-panel: #0f172a !important;
    --bg-panel-alt: #1e293b !important;
    --text-strong: #ffffff !important;
    --text-main: #e2e8f0 !important;
    --text-secondary: #94a3b8 !important;
    --text-muted: #64748b !important;
    --border-main: #2b3674 !important;
    --border-light: #1e293b !important;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.4) !important;
    --shadow-md: 0 10px 40px rgba(0,0,0,0.6) !important;
    --shadow-hover: 0 4px 20px rgba(0,0,0,0.6) !important;

    --accent-blue: #38bdf8 !important;
    --accent-green: #34d399 !important;
    --accent-red: #f87171 !important;
    --accent-amber: #fbbf24 !important;
    --accent-purple: #a78bfa !important;
    --accent-cyan: #22d3ee !important;

    /* Status Colors - Dark */
    --st-sup-bg: rgba(124, 58, 237, 0.15) !important; --st-sup-txt: #c4b5fd !important; --st-sup-border: rgba(124, 58, 237, 0.4) !important; 
    --st-fo-bg: rgba(5, 150, 105, 0.15) !important;   --st-fo-txt: #6ee7b7 !important;  --st-fo-border: rgba(5, 150, 105, 0.4) !important;   
    --st-rej-bg: rgba(220, 38, 38, 0.15) !important;  --st-rej-txt: #fca5a5 !important; --st-rej-border: rgba(220, 38, 38, 0.4) !important;  
    --st-pen-bg: rgba(217, 119, 6, 0.15) !important;  --st-pen-txt: #fcd34d !important; --st-pen-border: rgba(217, 119, 6, 0.4) !important;  
  }

  .nova-admin-wrapper {
    background-color: var(--bg-page);
    color: var(--text-main);
    min-height: 100vh;
    font-family: 'DM Sans', sans-serif;
    transition: background-color 0.3s ease, color 0.3s ease;
    padding: 30px 20px;
  }

  .nova-admin-wrapper * {
    box-sizing: border-box;
    transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;
  }

  .nv-card {
    background: var(--bg-card);
    border: 1px solid var(--border-main);
    border-radius: 16px;
    box-shadow: var(--shadow-sm);
  }

  .nv-hover-card:hover {
    border-color: var(--accent-blue);
    transform: translateY(-2px);
    box-shadow: var(--shadow-hover);
  }

  .nv-input {
    width: 100%;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid var(--border-main);
    background: var(--bg-panel);
    color: var(--text-strong);
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    outline: none;
  }
  .nv-input:focus { border-color: var(--accent-blue); }
  .nv-input::placeholder { color: var(--text-muted); }

  .nv-btn {
    padding: 10px 20px;
    border-radius: 10px;
    border: none;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .nv-btn-primary {
    background: linear-gradient(135deg, #2563EB, #38BDF8);
    color: #ffffff !important;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
  }
  .nv-btn-primary:hover:not(:disabled) { opacity: 0.9; }
  .nv-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

  @keyframes spin { to { transform: rotate(360deg); } }
  .nv-spin { animation: spin 0.8s linear infinite; }
  
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-thumb { background: var(--border-main); border-radius: 4px; }
`;

// 🛠️ SMART PICK FUNCTION
const pick = (obj, ...keys) => {
  if (!obj) return null;
  const safeObj = {};
  for (let k in obj) safeObj[k.toLowerCase().trim()] = obj[k];
  for (let key of keys) {
    const safeKey = key.toLowerCase().trim();
    if (safeObj[safeKey] !== undefined && safeObj[safeKey] !== null && String(safeObj[safeKey]).trim() !== "") {
      return safeObj[safeKey];
    }
  }
  return null;
};

// ─── DECISION CONFIG ───────────────────────────────────────────────────────────
const getDCfg = (d) => {
  const map = {
    Supervisor:     { label: 'Supervisor', bg: 'var(--st-sup-bg)', txt: 'var(--st-sup-txt)', border: 'var(--st-sup-border)', grad: 'var(--st-sup-grad)', icon: <Activity size={16}/> },
    Selected:       { label: 'Selected / Hired', bg: 'var(--st-fo-bg)', txt: 'var(--st-fo-txt)', border: 'var(--st-fo-border)', grad: 'var(--st-fo-grad)', icon: <CheckCircle size={16}/> },
    FO:             { label: 'Selected / Hired', bg: 'var(--st-fo-bg)', txt: 'var(--st-fo-txt)', border: 'var(--st-fo-border)', grad: 'var(--st-fo-grad)', icon: <CheckCircle size={16}/> },
    Hired:          { label: 'Selected / Hired', bg: 'var(--st-fo-bg)', txt: 'var(--st-fo-txt)', border: 'var(--st-fo-border)', grad: 'var(--st-fo-grad)', icon: <CheckCircle size={16}/> },
    'Not Selected': { label: 'Not Selected', bg: 'var(--st-rej-bg)', txt: 'var(--st-rej-txt)', border: 'var(--st-rej-border)', grad: 'var(--st-rej-grad)', icon: <XCircle size={16}/> },
    Rejected:       { label: 'Not Selected', bg: 'var(--st-rej-bg)', txt: 'var(--st-rej-txt)', border: 'var(--st-rej-border)', grad: 'var(--st-rej-grad)', icon: <XCircle size={16}/> },
    Pending:        { label: 'Pending', bg: 'var(--st-pen-bg)', txt: 'var(--st-pen-txt)', border: 'var(--st-pen-border)', grad: 'var(--st-pen-grad)', icon: <Clock size={16}/> },
  };
  // Handle variations
  for (let key in map) {
    if (String(d).toLowerCase().includes(key.toLowerCase())) return map[key];
  }
  return map['Pending'];
};

export default function AdminEmployeePage() {
  // Global Theme Observer
  const [isDark, setIsDark] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark') ||
             document.body.classList.contains('dark') ||
             document.documentElement.getAttribute('data-theme') === 'dark' ||
             document.body.getAttribute('data-theme') === 'dark';
    }
    return false;
  });

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(
        document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark') ||
        document.documentElement.getAttribute('data-theme') === 'dark' ||
        document.body.getAttribute('data-theme') === 'dark' ||
        document.body.classList.contains('dark-mode')
      );
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => observer.disconnect();
  }, []);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Search & Filter state
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');

  const [photo, setPhoto] = useState(null);            // displayed src (saved URL or local preview)
  const [photoSaved, setPhotoSaved] = useState(false); // true once persisted to the server
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const fileInputRef = useRef(null);

  // Main API base already includes /api; strip it when building image URLs.
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const IMG_BASE = API_BASE ? API_BASE.replace("/api", "") : "";

  const getSelectedCnic = () => {
    const c = pick(selectedEmployee, "CNIC | شناختی کارڈ");
    const s = c ? String(c).trim() : "";
    return s && s !== "-" ? s : "";
  };

  const fetchEmployees = () => {
    setLoading(true);
    setErrorMsg(null);
    fetch(SCRIPT_URL)
      .then(async (res) => {
        const text = await res.text();
        try {
          return JSON.parse(text);
        } catch (e) {
          throw new Error("API Issue: " + text.substring(0, 100));
        }
      })
      .then((json) => {
        if (json.success && json.data) {
          setEmployees(json.data.reverse());
        } else if (Array.isArray(json)) {
          setEmployees(json.reverse());
        } else if (json.data && Array.isArray(json.data)) {
          setEmployees(json.data.reverse());
        } else {
          setErrorMsg("Data fetch failed. Invalid format.");
        }
      })
      .catch((err) => setErrorMsg(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Load any saved profile photo for the opened employee (keyed by CNIC).
  useEffect(() => {
    setPhoto(null);
    setPhotoSaved(false);
    setPhotoError(null);
    if (!selectedEmployee) return;
    const cnic = getSelectedCnic();
    if (!cnic) return;
    let cancelled = false;
    fetch(`${API_BASE}/employee-photos/${encodeURIComponent(cnic)}`)
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        if (json.success && json.photo_url) {
          setPhoto(`${IMG_BASE}${json.photo_url}`);
          setPhotoSaved(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const cnic = getSelectedCnic();
    if (!cnic) { setPhotoError("This employee has no CNIC on record, so a photo can't be saved."); return; }
    if (!file.type.startsWith("image/")) { setPhotoError("Please choose an image file."); return; }

    // instant local preview while uploading
    setPhoto(URL.createObjectURL(file));
    setPhotoSaved(false);
    setUploadingPhoto(true);
    setPhotoError(null);

    try {
      const fd = new FormData();
      fd.append("photo", file);
      fd.append("cnic", cnic);
      const res = await fetch(`${API_BASE}/employee-photos`, { method: "POST", body: fd });
      const json = await res.json();
      if (json.success && json.photo_url) {
        setPhoto(`${IMG_BASE}${json.photo_url}`);
        setPhotoSaved(true);
      } else {
        setPhotoError(json.message || "Upload failed.");
      }
    } catch (err) {
      setPhotoError("Upload failed. Check your connection.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleBackToList = () => {
    setSelectedEmployee(null);
    setPhoto(null);
    setPhotoSaved(false);
    setUploadingPhoto(false);
    setPhotoError(null);
  };

  // 🛠️ FILTER LOGIC (FIXED String conversion to prevent toLowerCase errors)
  const filtered = employees.filter(row => {
    const d = String(pick(row, "Hiring Decision | بھرتی کا فیصلہ") || 'Pending');
    const matchF = filter === 'All' 
                   || d.toLowerCase().includes(filter.toLowerCase()) 
                   || (filter === 'Selected' && (d.toLowerCase().includes('fo') || d.toLowerCase().includes('hired')));
                   
    const n = String(pick(row, "Candidate Name | امیدوار کا نام") || '').toLowerCase();
    const p = String(pick(row, "Position | عہدہ") || '').toLowerCase();
    const c = String(pick(row, "CNIC | شناختی کارڈ") || '').toLowerCase();
    
    return matchF && (!search || n.includes(search.toLowerCase()) || p.includes(search.toLowerCase()) || c.includes(search.toLowerCase()));
  });

  const counts = {
    Total: employees.length,
    Selected: employees.filter(r => { const d = String(pick(r, "Hiring Decision | بھرتی کا فیصلہ") || ''); return d.toLowerCase().includes('selected') || d.toLowerCase().includes('fo') || d.toLowerCase().includes('hired'); }).length,
    Pending: employees.filter(r => { const d = String(pick(r, "Hiring Decision | بھرتی کا فیصلہ") || ''); return d.toLowerCase().includes('pending'); }).length,
    Rejected: employees.filter(r => { const d = String(pick(r, "Hiring Decision | بھرتی کا فیصلہ") || ''); return d.toLowerCase().includes('not') || d.toLowerCase().includes('reject'); }).length,
  };

  return (
    <div className="nova-admin-wrapper">
      <style>{themeStyles}</style>
      
      {!selectedEmployee ? (
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          
          {/* PAGE HEADER */}
          <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '800', color: 'var(--accent-blue)', letterSpacing: '0.1em', marginBottom: '4px', textTransform: 'uppercase' }}>Human Resources</div>
              <h1 style={{ fontSize: '28px', fontWeight: '800', color: 'var(--text-strong)', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Users size={28} color="var(--accent-green)" /> Field Evaluations & Profiles
              </h1>
              <p style={{ color: 'var(--text-secondary)', marginTop: '8px', fontSize: '14px' }}>
                Review onboarding profiles, evaluation scores, and manage field personnel records.
              </p>
            </div>
            <button className="nv-btn nv-btn-primary" onClick={fetchEmployees} disabled={loading}>
              {loading ? <><span className="nv-spin" style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block' }} /> Syncing...</> : 'Sync Records'}
            </button>
          </div>

          {/* STAT CARDS */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
            {[
              { label: 'TOTAL EVALUATIONS', count: counts.Total, icon: <Users size={20}/>, color: 'var(--accent-blue)' },
              { label: 'HIRED / SELECTED', count: counts.Selected, icon: <CheckCircle size={20}/>, color: 'var(--accent-green)' },
              { label: 'PENDING REVIEW', count: counts.Pending, icon: <Clock size={20}/>, color: 'var(--accent-amber)' },
              { label: 'NOT SELECTED', count: counts.Rejected, icon: <XCircle size={20}/>, color: 'var(--accent-red)' },
            ].map((s, i) => (
              <div key={i} className="nv-card nv-hover-card" style={{ padding: '20px 24px', borderTop: `3px solid ${s.color}`, cursor: 'pointer' }} onClick={() => setFilter(s.label.includes('TOTAL') ? 'All' : s.label.includes('HIRED') ? 'Selected' : s.label.includes('PENDING') ? 'Pending' : 'Not')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '12px' }}>
                  <span style={{ color: s.color }}>{s.icon}</span> {s.label}
                </div>
                <div style={{ fontSize: '36px', fontWeight: '800', color: 'var(--text-strong)', lineHeight: 1, fontFamily: "'DM Mono', monospace" }}>{s.count}</div>
              </div>
            ))}
          </div>

          {/* SEARCH + FILTER */}
          <div className="nv-card" style={{ padding: '16px 20px', marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '260px', maxWidth: '400px' }}>
              <Search size={16} color="var(--text-muted)" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
              <input 
                className="nv-input" 
                style={{ paddingLeft: '40px' }}
                value={search} 
                onChange={e => setSearch(e.target.value)} 
                placeholder="Search by name, CNIC or role..."
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { val: 'All', label: `All`, color: 'var(--accent-blue)', bg: 'rgba(37,99,235,0.1)' },
                { val: 'Selected', label: `Hired / Selected`, color: 'var(--accent-green)', bg: 'rgba(5,150,105,0.1)' },
                { val: 'Pending', label: `Pending`, color: 'var(--accent-amber)', bg: 'rgba(217,119,6,0.1)' },
                { val: 'Not', label: `Not Selected`, color: 'var(--accent-red)', bg: 'rgba(220,38,38,0.1)' },
              ].map(btn => (
                <button key={btn.val} onClick={() => setFilter(btn.val)} style={{
                  padding: '8px 16px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.2s',
                  border: `1px solid ${filter === btn.val ? btn.color : 'var(--border-main)'}`,
                  background: filter === btn.val ? btn.bg : 'var(--bg-card)',
                  color: filter === btn.val ? btn.color : 'var(--text-secondary)',
                  fontWeight: filter === btn.val ? '700' : '600',
                }}>{btn.label}</button>
              ))}
            </div>
          </div>

          {errorMsg && (
            <div style={{ background: 'var(--st-rej-bg)', border: '1px solid var(--st-rej-border)', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', color: 'var(--st-rej-txt)', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AlertTriangle size={18} /> {errorMsg}
            </div>
          )}

          <div className="nv-card" style={{ overflow: "hidden" }}>
            {loading ? ( 
              <div style={{ padding: "80px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", color: "var(--text-muted)" }}>
                <div className="nv-spin" style={{ width: 36, height: 36, border: '3px solid var(--border-main)', borderTopColor: 'var(--accent-blue)', borderRadius: '50%' }} />
                <div style={{ fontSize: '14px', fontWeight: '700', letterSpacing: '0.05em' }}>SYNCING RECORDS...</div>
              </div>
            ) : filtered.length === 0 && !errorMsg ? (
              <div style={{ padding: "80px 20px", textAlign: "center", color: "var(--text-muted)", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
                <Users size={48} color="var(--border-main)" />
                <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-main)' }}>No records found</div>
                <div style={{ fontSize: '13px' }}>Try adjusting your search or filter criteria.</div>
              </div>
            ) : filtered.length > 0 ? (
              <div style={{ width: "100%", overflowX: "auto" }}>
                <div style={{ minWidth: "800px" }}>
                  {/* Table Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 2.5fr 1.5fr 1.5fr 1.5fr 1fr', padding: '16px 24px', background: 'var(--bg-panel)', borderBottom: '1px solid var(--border-main)', fontWeight: '700', fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    <div>#</div>
                    <div>Candidate Info</div>
                    <div>Role / Position</div>
                    <div>Site / Area</div>
                    <div>Status</div>
                    <div style={{ textAlign: 'right' }}>Action</div>
                  </div>

                  {/* Rows */}
                  {filtered.map((emp, idx) => {
                    const name = String(pick(emp, "Candidate Name | امیدوار کا نام") || "Unknown");
                    const role = String(pick(emp, "Position | عہدہ") || "-");
                    const site = String(pick(emp, "Area/Site | علاقہ / سائٹ") || "-");
                    const status = String(pick(emp, "Hiring Decision | بھرتی کا فیصلہ") || "Pending");
                    const date = String(pick(emp, "Date | تاریخ") || pick(emp, "Submitted At | جمع کرانے کا وقت") || "—");
                    const dc = getDCfg(status);

                    return (
                      <div 
                        key={idx} 
                        style={{ display: 'grid', gridTemplateColumns: '0.5fr 2.5fr 1.5fr 1.5fr 1.5fr 1fr', padding: '16px 24px', borderBottom: '1px solid var(--border-light)', alignItems: 'center', transition: 'background 0.2s', cursor: 'pointer' }} 
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-panel-alt)'} 
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => setSelectedEmployee(emp)}
                      >
                        <div style={{ color: 'var(--text-muted)', fontWeight: '700', fontFamily: "'DM Mono', monospace" }}>{filtered.length - idx}</div>
                        
                        <div>
                          <div style={{ fontWeight: '800', color: 'var(--text-strong)', marginBottom: '4px', fontSize: '15px' }}>{name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Clock size={11} /> {date}
                          </div>
                        </div>

                        <div>
                          <span style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-main)', color: 'var(--text-strong)', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700' }}>
                            {role}
                          </span>
                        </div>

                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '500' }}>
                          <MapPin size={13} color="var(--text-muted)" /> {site}
                        </div>

                        <div>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: dc.bg, color: dc.txt, padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '800', border: `1px solid ${dc.border}` }}>
                            {React.cloneElement(dc.icon, { size: 14 })} {dc.label}
                          </span>
                        </div>

                        <div style={{ textAlign: "right" }}>
                          <button style={{ backgroundColor: 'var(--bg-page)', color: 'var(--text-strong)', border: '1px solid var(--border-main)', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s' }}>
                            View Profile
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div style={{ maxWidth: "960px", margin: "0 auto" }}>
          
          <button 
            onClick={handleBackToList}
            style={{ background: 'var(--bg-card)', color: 'var(--text-main)', border: '1px solid var(--border-main)', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', boxShadow: 'var(--shadow-sm)' }}
          >
            <ArrowLeft size={16} /> Back to Directory | واپس جائیں
          </button>

          {(() => {
            const p_name = String(pick(selectedEmployee, "Candidate Name | امیدوار کا نام") || "Unknown");
            const p_role = String(pick(selectedEmployee, "Position | عہدہ") || "-");
            const p_site = String(pick(selectedEmployee, "Area/Site | علاقہ / سائٹ") || "-");
            const p_status = String(pick(selectedEmployee, "Hiring Decision | بھرتی کا فیصلہ") || "Pending");
            const p_phone = String(pick(selectedEmployee, "Contact No. | رابطہ نمبر") || "-");
            const p_cnic = String(pick(selectedEmployee, "CNIC | شناختی کارڈ") || "-");
            const p_education = String(pick(selectedEmployee, "Q3. Qualification | تعلیمی قابلیت") || "-");
            const p_experience = String(pick(selectedEmployee, "Q2. Work Experience | کام کا تجربہ") || "-");
            
            const p_totalScore = String(pick(selectedEmployee, "Total Score | کل نمبر") || "0");
            const p_outOf = String(pick(selectedEmployee, "Out Of | کل ممکنہ نمبر") || "100");
            const p_remarks = String(pick(selectedEmployee, "Remarks | ریمارکس") || "No remarks provided.");
            const p_evaluator = String(pick(selectedEmployee, "Evaluator Name | جائزہ لینے والے کا نام") || "-");
            const p_date = String(pick(selectedEmployee, "Date | تاریخ") || pick(selectedEmployee, "Submitted At | جمع کرانے کا وقت") || "-");
            
            const dc = getDCfg(p_status);

            return (
              <>
                {photoError && (
                  <div style={{ background: 'var(--st-rej-bg)', border: '1px solid var(--st-rej-border)', borderRadius: '12px', padding: '12px 16px', color: 'var(--st-rej-txt)', fontSize: '13px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AlertTriangle size={16} /> {photoError}
                  </div>
                )}
                {!photoSaved && getSelectedCnic() && (
                  <div style={{ background: 'var(--st-pen-bg)', border: '1px solid var(--st-pen-border)', borderRadius: '12px', padding: '12px 16px', color: 'var(--st-pen-txt)', fontSize: '13px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AlertTriangle size={16} /> Profile picture is required — click the avatar to upload.
                  </div>
                )}
                {!getSelectedCnic() && (
                  <div style={{ background: 'var(--st-pen-bg)', border: '1px solid var(--st-pen-border)', borderRadius: '12px', padding: '12px 16px', color: 'var(--st-pen-txt)', fontSize: '13px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AlertTriangle size={16} /> No CNIC on record for this employee — a profile picture can't be saved until a CNIC exists.
                  </div>
                )}

                {/* HEADER CARD */}
                <div className="nv-card" style={{ overflow: 'hidden', marginBottom: '24px' }}>
                  <div style={{ background: dc.grad, padding: '32px 36px', display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                    
                    {/* PHOTO UPLOADER WIDGET */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                      <div
                        onClick={() => { if (getSelectedCnic()) fileInputRef.current.click(); }}
                        style={{ 
                          width: "96px", height: "96px", borderRadius: "50%", 
                          backgroundColor: "rgba(255,255,255,0.2)", 
                          display: "flex", alignItems: "center", justifyContent: "center", 
                          cursor: getSelectedCnic() ? "pointer" : "not-allowed", 
                          overflow: "hidden", position: "relative", 
                          border: `3px solid ${photoSaved ? "rgba(255,255,255,0.8)" : "var(--accent-red)"}`, 
                          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                          transition: "all 0.2s"
                        }}
                        onMouseEnter={e => { if(getSelectedCnic()) e.currentTarget.style.transform = 'scale(1.05)' }}
                        onMouseLeave={e => { if(getSelectedCnic()) e.currentTarget.style.transform = 'scale(1)' }}
                      >
                        {photo ? (
                          <img src={photo} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <span style={{ fontSize: "36px", color: "#ffffff", fontWeight: "800" }}>{p_name.charAt(0).toUpperCase()}</span>
                        )}
                        <div style={{ position: "absolute", bottom: 0, width: "100%", backgroundColor: "rgba(0,0,0,0.6)", color: "#fff", fontSize: "10px", textAlign: "center", padding: "6px 0", fontWeight: "700", letterSpacing: '0.5px' }}>
                          {uploadingPhoto ? "UPLOADING" : photoSaved ? "EDIT" : "UPLOAD"}
                        </div>
                      </div>
                      <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageChange} style={{ display: "none" }} />
                    </div>

                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '24px', fontWeight: '800', color: 'white' }}>{p_name}</div>
                      <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500' }}>
                        <Briefcase size={14} /> {p_role} 
                        <span style={{opacity: 0.5}}>|</span> 
                        <MapPin size={14} /> {p_site}
                      </div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.95)', color: '#000', padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '800', marginTop: '12px' }}>
                        {React.cloneElement(dc.icon, { size: 14 })} {dc.label}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right', background: 'rgba(0,0,0,0.15)', padding: '16px 24px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.2)' }}>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px', fontWeight: '700' }}>Evaluation Score</div>
                      <div style={{ fontSize: '36px', fontWeight: '900', color: 'white', lineHeight: 1, fontFamily: "'DM Mono', monospace" }}>
                        {p_totalScore}<span style={{ fontSize: '18px', opacity: 0.7, fontWeight: '500' }}>/{p_outOf}</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: '36px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                      
                      {/* Left Column */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <div style={{ padding: "24px", backgroundColor: "var(--bg-panel)", borderRadius: "12px", border: "1px solid var(--border-main)" }}>
                          <h3 style={{ borderBottom: "1px solid var(--border-light)", paddingBottom: "12px", marginTop: 0, color: "var(--text-strong)", fontSize: "16px", display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <UserCheck size={18} color="var(--accent-blue)" /> Personal Details
                          </h3>
                          <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px", fontSize: "14px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "var(--text-secondary)", fontWeight: '600' }}>Contact No.</span> <span style={{ fontWeight: "700", color: "var(--text-strong)", display: 'flex', alignItems: 'center', gap: '6px' }}><Phone size={14} color="var(--text-muted)"/> {p_phone}</span></div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "var(--text-secondary)", fontWeight: '600' }}>CNIC</span> <span style={{ fontWeight: "700", fontFamily: "'DM Mono', monospace", fontSize: "13px", color: "var(--text-strong)" }}>{p_cnic}</span></div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "var(--text-secondary)", fontWeight: '600' }}>Date applied</span> <span style={{ fontWeight: "700", color: "var(--text-strong)", display: 'flex', alignItems: 'center', gap: '6px' }}><Calendar size={14} color="var(--text-muted)"/> {p_date}</span></div>
                          </div>
                        </div>

                        <div style={{ padding: "24px", backgroundColor: "var(--bg-panel)", borderRadius: "12px", border: "1px solid var(--border-main)" }}>
                          <h3 style={{ borderBottom: "1px solid var(--border-light)", paddingBottom: "12px", marginTop: 0, color: "var(--text-strong)", fontSize: "16px", display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <BookOpen size={18} color="var(--accent-purple)" /> Professional Info
                          </h3>
                          <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px", fontSize: "14px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: 'flex-start' }}><span style={{ color: "var(--text-secondary)", fontWeight: '600' }}>Education</span> <span style={{ fontWeight: "700", textAlign: "right", maxWidth: "60%", color: "var(--text-strong)" }}>{p_education}</span></div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: 'flex-start' }}><span style={{ color: "var(--text-secondary)", fontWeight: '600' }}>Experience</span> <span style={{ fontWeight: "700", textAlign: "right", maxWidth: "60%", color: "var(--text-strong)" }}>{p_experience}</span></div>
                          </div>
                        </div>
                      </div>

                      {/* Right Column */}
                      <div style={{ padding: "24px", backgroundColor: "var(--bg-panel)", borderRadius: "12px", border: "1px solid var(--border-main)", height: '100%' }}>
                        <h3 style={{ borderBottom: "1px solid var(--border-light)", paddingBottom: "12px", marginTop: 0, color: "var(--text-strong)", fontSize: "16px", display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FileText size={18} color="var(--accent-amber)" /> Evaluation Comments & Remarks
                        </h3>
                        <div style={{ marginTop: "20px" }}>
                          <div style={{ padding: "20px", backgroundColor: "var(--bg-card)", borderRadius: "10px", borderLeft: "4px solid var(--accent-amber)", borderTop: "1px solid var(--border-main)", borderRight: "1px solid var(--border-main)", borderBottom: "1px solid var(--border-main)" }}>
                            <p style={{ margin: "0 0 16px 0", color: "var(--text-strong)", fontSize: "15px", fontStyle: "italic", lineHeight: "1.6", fontWeight: "500" }}>"{p_remarks}"</p>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border-light)", paddingTop: "16px", fontSize: "13px" }}>
                              <span style={{ color: "var(--text-secondary)" }}>Evaluated By: <strong style={{ color: "var(--text-strong)" }}>{p_evaluator}</strong></span>
                              <span style={{ color: "var(--text-secondary)" }}>{p_date}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}